import { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";

import { db } from "../../../backend/firebaseConfig";
import { LEAVE_STATUS } from "../utils/staffHolidayConstants";

const STAFF_COLLECTION_PATH = ["Authorized Members", "Staff", "Data"];

const normaliseEmail = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const toDateKey = (value) => {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    const date = value.toDate();

    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
  }

  if (value instanceof Date) {
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, "0"),
      String(value.getDate()).padStart(2, "0"),
    ].join("-");
  }

  const raw = String(value).trim();

  const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoMatch) {
    return [
      isoMatch[1],
      String(isoMatch[2]).padStart(2, "0"),
      String(isoMatch[3]).padStart(2, "0"),
    ].join("-");
  }

  const ukMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ukMatch) {
    return [
      ukMatch[3],
      String(ukMatch[2]).padStart(2, "0"),
      String(ukMatch[1]).padStart(2, "0"),
    ].join("-");
  }

  return "";
};

const getStaffDisplayName = (staff = {}) => {
  return (
    staff.displayName ||
    staff.name ||
    [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
    staff.email ||
    staff.id ||
    ""
  );
};

const NO_DEPARTMENT_VALUE = "__NO_DEPARTMENT__";

const normaliseDepartmentId = (value = "") => {
  const raw = String(value || "").trim();

  if (!raw) return NO_DEPARTMENT_VALUE;

  return raw.toLowerCase().replace(/\s+/g, "-");
};

const toTitleCase = (value = "") => {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const normaliseDepartmentItem = (department) => {
  if (!department) return null;

  if (typeof department === "string") {
    const name = toTitleCase(department);

    return {
      id: normaliseDepartmentId(department),
      name,
    };
  }

  const rawId =
    department.id ||
    department.departmentId ||
    department.value ||
    department.name ||
    department.departmentName ||
    "";

  const rawName =
    department.name ||
    department.departmentName ||
    department.label ||
    rawId ||
    "";

  if (!rawId && !rawName) return null;

  return {
    id: normaliseDepartmentId(rawId || rawName),
    name: toTitleCase(rawName || rawId),
  };
};

const getStaffDepartments = (staff = {}) => {
  const departments = [];

  if (Array.isArray(staff.departments)) {
    staff.departments.forEach((department) => {
      const normalised = normaliseDepartmentItem(department);

      if (normalised) {
        departments.push(normalised);
      }
    });
  }

  if (staff.departmentId || staff.departmentName) {
    const normalised = normaliseDepartmentItem({
      id: staff.departmentId || staff.departmentName,
      name: staff.departmentName || staff.departmentId,
    });

    if (normalised) {
      departments.push(normalised);
    }
  }

  const unique = new Map();

  departments.forEach((department) => {
    unique.set(department.id, department);
  });

  return Array.from(unique.values());
};

const getPrimaryDepartment = (staff = {}) => {
  const departments = getStaffDepartments(staff);

  return (
    departments[0] || {
      id: NO_DEPARTMENT_VALUE,
      name: "No Department",
    }
  );
};

const isApprovedRequest = (request = {}) => {
  const status = String(request.status || "").toLowerCase();

  return status === String(LEAVE_STATUS?.APPROVED || "approved").toLowerCase();
};

const isRequestInYear = (request = {}, year) => {
  const startDate = toDateKey(request.startDate);
  const endDate = toDateKey(request.endDate || request.startDate);

  if (!startDate && !endDate) return false;

  return (
    Number(String(startDate || endDate).slice(0, 4)) === Number(year) ||
    Number(String(endDate || startDate).slice(0, 4)) === Number(year)
  );
};

const getDayValueFromIncludedDate = (item = {}, fallback = 1) => {
  const value = Number(
    item.value ?? item.dayValue ?? item.totalDays ?? item.days ?? fallback,
  );

  return Number.isFinite(value) && value > 0 ? value : fallback;
};

const buildEventsFromRequest = (request = {}) => {
  if (!isApprovedRequest(request)) return [];

  if (Array.isArray(request.includedDates) && request.includedDates.length) {
    return request.includedDates
      .map((item) => {
        const date = toDateKey(item?.date || item?.dateKey || item);

        if (!date) return null;

        return {
          eventId: `${request.requestId || request.id}-${date}`,
          requestId: request.requestId || request.id || "",
          date,
          startDate: request.startDate,
          endDate: request.endDate,
          leaveType: request.leaveType,
          dayValue: getDayValueFromIncludedDate(item),
          totalDays: getDayValueFromIncludedDate(item),
          status: request.status,
          source: "leave_request",
          isTrackerImport: Boolean(request.isTrackerImport),
          importedFrom: request.importedFrom || "",
          importedYear: request.importedYear || null,
        };
      })
      .filter(Boolean);
  }

  const date = toDateKey(request.startDate || request.date);

  if (!date) return [];

  const totalDays = Number(request.totalDays || request.dayValue || 1);

  return [
    {
      eventId: `${request.requestId || request.id}-${date}`,
      requestId: request.requestId || request.id || "",
      date,
      startDate: request.startDate || date,
      endDate: request.endDate || date,
      leaveType: request.leaveType,
      dayValue: Number.isFinite(totalDays) && totalDays > 0 ? totalDays : 1,
      totalDays: Number.isFinite(totalDays) && totalDays > 0 ? totalDays : 1,
      status: request.status,
      source: "leave_request",
      isTrackerImport: Boolean(request.isTrackerImport),
      importedFrom: request.importedFrom || "",
      importedYear: request.importedYear || null,
    },
  ];
};

const fetchStaffDirectory = async () => {
  const staffRef = collection(db, ...STAFF_COLLECTION_PATH);
  const snapshot = await getDocs(staffRef);

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    email: docSnap.id,
    ...docSnap.data(),
  }));
};

const fetchStaffLeaveRequests = async ({ staffEmail, year }) => {
  if (!staffEmail || !year) return [];

  const requestsRef = collection(
    db,
    ...STAFF_COLLECTION_PATH,
    staffEmail,
    "LeaveRequests",
  );

  const snapshot = await getDocs(requestsRef);

  return snapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      requestId: docSnap.id,
      ...docSnap.data(),
    }))
    .filter((request) => isApprovedRequest(request))
    .filter((request) => isRequestInYear(request, year));
};

export const useStaffHolidayTracker = ({
  year,
  departmentId = "all",
  staffEmail = "",
}) => {
  const [staffRows, setStaffRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(Boolean(year));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadTracker = async () => {
      if (!year) {
        setStaffRows([]);
        setDepartments([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const staffDirectory = await fetchStaffDirectory();
        const normalisedStaffEmail = normaliseEmail(staffEmail);

        const allDepartmentsMap = new Map();

        staffDirectory.forEach((staff) => {
          getStaffDepartments(staff).forEach((department) => {
            if (!department.id) return;

            allDepartmentsMap.set(String(department.id), {
              id: String(department.id),
              name: department.name || String(department.id),
            });
          });
        });

        const relevantStaffDirectory = normalisedStaffEmail
          ? staffDirectory.filter(
              (staff) =>
                normaliseEmail(staff.email || staff.id) === normalisedStaffEmail,
            )
          : staffDirectory;

        const rows = await Promise.all(
          relevantStaffDirectory.map(async (staff) => {
            const staffEmail = normaliseEmail(staff.email || staff.id);
            const primaryDepartment = getPrimaryDepartment(staff);

            const requests = await fetchStaffLeaveRequests({
              staffEmail,
              year,
            });

            const events = requests
              .flatMap(buildEventsFromRequest)
              .filter((event) => {
                return Number(String(event.date).slice(0, 4)) === Number(year);
              })
              .sort((a, b) => String(a.date).localeCompare(String(b.date)));

            return {
              staffEmail,
              staffName: getStaffDisplayName(staff),
              departmentId: primaryDepartment.id,
              departmentName: primaryDepartment.name,
              departments: getStaffDepartments(staff),
              requests,
              events,
            };
          }),
        );

        const filteredRows = rows
          .filter((row) => normalisedStaffEmail || row.events.length > 0)
          .filter((row) => {
            if (departmentId === "all") return true;

            return row.departments.some(
              (department) => String(department.id) === String(departmentId),
            );
          })
          .sort((a, b) =>
            String(a.staffName).localeCompare(String(b.staffName)),
          );

        if (!cancelled) {
          setStaffRows(filteredRows);
          setDepartments(
            Array.from(allDepartmentsMap.values()).sort((a, b) =>
              String(a.name).localeCompare(String(b.name)),
            ),
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setStaffRows([]);
          setDepartments([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadTracker();

    return () => {
      cancelled = true;
    };
  }, [year, departmentId, staffEmail]);

  return {
    staffRows,
    departments,
    loading,
    error,
  };
};
